import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, KeyRound, Trash2, IdCard } from "lucide-react";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", username: "", password: "" });
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = async () => { const { data } = await api.get("/users"); setUsers(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/users", form); toast.success("Labour user created"); setCreating(false); setForm({ name: "", username: "", password: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const resetPw = async () => {
    try { await api.put(`/users/${pwFor.id}`, { password: newPw }); toast.success("Password updated"); setPwFor(null); setNewPw(""); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/users/${deleting.id}`); toast.success("Labour user removed (their sales are preserved)"); setDeleting(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-chivo font-black text-3xl text-slate-900">Labour Users</h1>
          <p className="text-slate-500 mt-1">Create, deactivate or remove factory logins. Removing a user keeps all their sales.</p>
        </div>
        <Button data-testid="add-user-button" className="bg-sky-600 hover:bg-sky-700" onClick={() => setCreating(true)}><Plus className="h-4 w-4 mr-1" /> Add Labour</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-slate-50 text-slate-500 text-left border-b border-slate-200">
            <tr><th className="p-3 font-semibold">Name</th><th className="p-3 font-semibold">Username</th><th className="p-3 font-semibold">Created</th><th className="p-3 font-semibold">Active</th><th className="p-3 text-right font-semibold">Actions</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-sky-50/40 transition-colors" data-testid={`user-row-${u.id}`}>
                <td className="p-3 font-medium text-slate-900">{u.name}</td>
                <td className="p-3 text-slate-500">{u.username}</td>
                <td className="p-3 text-slate-500">{fmtDate(u.created_at)}</td>
                <td className="p-3"><Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`} /></td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" data-testid={`reset-pw-${u.id}`} onClick={() => setPwFor(u)}><KeyRound className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" data-testid={`delete-user-${u.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleting(u)}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-slate-400"><IdCard className="h-8 w-8 mx-auto mb-2 opacity-40" />No labour users yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader><DialogTitle>Add Labour User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full Name</Label><Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Username</Label><Input data-testid="user-username-input" autoCapitalize="none" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></div>
            <div><Label>Password</Label><Input data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button><Button data-testid="save-user-button" className="bg-sky-600 hover:bg-sky-700" onClick={create}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwFor} onOpenChange={(o) => !o && setPwFor(null)}>
        <DialogContent data-testid="reset-pw-dialog">
          <DialogHeader><DialogTitle>Reset Password — {pwFor?.name}</DialogTitle></DialogHeader>
          <div><Label>New Password</Label><Input data-testid="new-password-input" value={newPw} onChange={(e) => setNewPw(e.target.value)} /></div>
          <DialogFooter><Button variant="outline" onClick={() => setPwFor(null)}>Cancel</Button><Button data-testid="save-pw-button" className="bg-sky-600 hover:bg-sky-700" onClick={resetPw}>Update</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-user-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove labour user {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Their login will be removed and they can no longer sign in. All sales they previously entered stay safe and remain visible in Sales & Reports.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user-button" className="bg-red-600 hover:bg-red-700" onClick={doDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
